import React, { Fragment } from 'react';
import isEmpty from 'lodash/isEmpty';
import { useTranslation } from 'react-i18next';

import {
  Form,
  ButtonsWrapper,
  Button,
  TextInput,
  SelectInput,
  UploadInput,
  Typography,
  Icon,
  ICONS,
  PLATFORM_THEME_COLORS,
} from '@thinknet-gmbh/platform-common-ui';

import { useGetAllDocumentImporterTemplates } from 'src/hooks/document-importer-template/get-all-document-importer-templates.hook';
import { useGetDocumentLabelsByCompany } from 'src/hooks/documents-labels/get-documents-labels.hook';

import { DOCUMENT_FILE_MAX_SIZE } from '../../document-importer-list.constants';
import { IDocumentImporterItem } from '../../document-importer-list.interfaces';
import { LabelFilesInfo, StyledDocumentImporterForm } from './document-importer-form.styles';


const DocumentImporterForm = ({
  onClose,
  currentCompanyId,
  onFinishHandler,
}: {
  onClose: () => void;
  onFinishHandler: (values: IDocumentImporterItem) => void;
  currentCompanyId?: string;
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const { loading, importerTemplates } = useGetAllDocumentImporterTemplates();
  const { documentsLabels } = useGetDocumentLabelsByCompany(
    currentCompanyId
  );

  return (
    <StyledDocumentImporterForm
      layout="vertical"
      form={form}
      onFinish={values => onFinishHandler(values)}
      initialValues={{}}
    >
      <Form.Item
        name="title"
        label={t('documentImporter:labels.title')}
        rules={[{ required: true, message: t('customers:error.fieldIsRequired') }]}
      >
        <TextInput placeholder={t('documentImporter:labels.titlePlaceholder')} maxLength={50} />
      </Form.Item>

      {!loading && (
        <Fragment>
          <Form.Item
            name="documentImporterTemplateId"
            label={t('documentImporter:labels.importPattern')}
            rules={[{ required: true, message: t('common:validation.error.fieldRequired') }]}
          >
            <SelectInput placeholder={t('documentImporter:labels.importPatternPlaceholder')}>
              {importerTemplates?.map(({ _id, title }: { _id: string; title: string }) => (
                <SelectInput.Option key={_id} value={_id}>
                  {title}
                </SelectInput.Option>
              ))}
            </SelectInput>
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue, setFieldsValue }) => {
              const { fileList } = getFieldValue('importedDocuments') || {};

              return (
                <Form.Item
                  name="importedDocuments"
                  className={'upload-files-field'}
                  valuePropName="file"
                  rules={[
                    {
                      required: true,
                      message: t('common:validation.error.fieldRequired'),
                    },
                    () => ({
                      validator(_, { file }) {
                        if (file.size > DOCUMENT_FILE_MAX_SIZE) {
                          return Promise.reject(
                            new Error(t('settings:documents.errors.wrongFileSize', { detail: 5 }))
                          );
                        }

                        return Promise.resolve();
                      },
                    }),
                  ]}
                  label={t('documentImporter:labels.selectFiles')}
                >
                  {isEmpty(fileList) ? (
                    <UploadInput
                      showUploadList={false}
                      beforeUpload={() => false}
                      variant="button"
                      multiple={true}
                      btnText={t('documentImporter:buttons.uploadFiles')}
                      accept=".jpeg, .jpg, .png, .pdf, .doc, .docx .xlsx"
                      maxCount={50}
                      defaultFileList={undefined}
                    />
                  ) : (
                    <LabelFilesInfo>
                      <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                        {t(
                          `documentImporter:labels.${
                            fileList.length > 1 ? 'filesSelectedNumbers' : 'fileSelectedNumber'
                          }`,
                          {
                            number: fileList.length,
                          }
                        )}
                      </Typography>
                      <Icon
                        onClick={() => {
                          setFieldsValue({
                            importedDocuments: undefined,
                          });
                        }}
                        icon={ICONS.trash}
                        color={PLATFORM_THEME_COLORS.tone.red}
                        rightMargin={false}
                      />
                    </LabelFilesInfo>
                  )}
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item
            name="documentLabelId"
            label={t('documentImporter:labels.documentType')}
            rules={[
              {
                required: true,
                message: t('common:validation.error.fieldRequired'),
              },
            ]}
          >
            <SelectInput placeholder={t('documentImporter:labels.selectDocumentType')}>
              {documentsLabels.map(({ _id, name }: {_id: string, name: string}) => (
                <SelectInput.Option key={_id} value={_id}>
                  {name}
                </SelectInput.Option>
              ))}
            </SelectInput>
          </Form.Item>

          <Form.Item
            name="description"
            label={t('documentImporter:labels.description')}
            rules={[{ required: true, message: t('customers:error.fieldIsRequired') }]}
          >
            <TextInput placeholder={'November 2024'} maxLength={100} />
          </Form.Item>
        </Fragment>
      )}

      <ButtonsWrapper marginTop={16}>
        <Button type="primary" htmlType="submit">
          {t('common:buttons.start')}
        </Button>
        <Button onClick={onClose}>{t('common:buttons.cancel')}</Button>
      </ButtonsWrapper>
    </StyledDocumentImporterForm>
  );
};

export default DocumentImporterForm;
